<template>
  <div ref="defineTab">
    <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.labelable)" id="pn-label">
      <panel-item>
        <label :for="selectedObjectModelProperty.idForProp('label')">Label</label>
        <expandable-textarea
          :id="selectedObjectModelProperty.idForProp('label')"
          v-model="selectedObjectModelProperty.label_title"
          placeholder="Enter a label..."></expandable-textarea>
      </panel-item>
    </panel-block>

    <aggregatable-config
      @input="onAggregateSelection"
      v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.aggregatable)"
      id="aggregatable-config"
      :model="selectedObjectModelProperty"
    >
    </aggregatable-config>
    <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.linkToRelationable)" id="pn-linked-relationship">
      <panel-item>
        <label :for="selectedObjectModelProperty.idForProp('linked_relation_client_id')" class="a-select-label">Relationship</label>
        <config-field-validation :model="selectedObjectModelProperty" field-name="linked_relation_client_id">
          <a-select
            :id="selectedObjectModelProperty.idForProp('linked_relation_client_id')"
            :options="relationSearchOptions"
            compress
            force-nullify-if-null
            bind-by-object
            :value="getRelationOption()"
            @input="setRelationOption"
          ></a-select>
        </config-field-validation>
      </panel-item>
      <template v-for="relationObjectModel in linkedObjectModels">
        <template v-if="showConfigureMappedProperty">
          <label
            :for="relationObjectModel.idForProp('linked_relation_mapped_properties')"
            class="a-select-label">
            {{`${relationObjectModel.title}'s Mapped Property`}}
          </label>
          <config-field-validation :model="selectedObjectModelProperty" :field-name="relationObjectModel.idForProp('linked_relation_mapped_properties')">
            <a-select
              :id="relationObjectModel.idForProp('linked_relation_mapped_properties')"
              @input="addLinkedPropertyRelation($event, relationObjectModel)"
              :options="objectModelPropertyWithDefaultVOptionSetsFor(relationObjectModel)"
              compress
              bind-by-object
              force-nullify-if-null
              :value="mappedPropertyValue(relationObjectModel)"
            ></a-select>
          </config-field-validation>
        </template>
        <panel-item  v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.aggregatable) && linkedRelationMappedProperty(relationObjectModel)">
          <label
            :for="relationObjectModel.idForProp('linked_relation_has_filter')"
            class="a-select-label">
            Do you want to add a filter?
          </label>
          <yes-no-select :id="relationObjectModel.idForProp('linked_relation_has_filter')"
                         compress
                         v-model="linkedRelationMappedProperty(relationObjectModel).linked_property_has_filter"
                         :value="aggregatePropertyHasFilter(relationObjectModel)"></yes-no-select>
          <panel-item v-if="aggregatePropertyHasFilter(relationObjectModel)">
            <conditional-wrapper
              :parent="linkedRelationMappedProperty(relationObjectModel)"
              :field-definition="ObjectModelConditionalFields.AGGREGATE_FILTER"
              :has-otherwise-value="false"
              :conditional-rule-options="conditionalRuleOptions"
              :can-add-rule="Object.keys(linkedRelationMappedProperty(relationObjectModel).filter_conditional_rule_sets).length === 0">
              <template #value="slotProps">
                <input type="hidden"
                       v-model="slotProps.model[slotProps.fieldName]"
                       value="true">
              </template>
            </conditional-wrapper>
          </panel-item>
        </panel-item>
        <panel-item v-if="displayNullOptions">
          <label
            :for="selectedObjectModelProperty.idForProp('linked_relation_has_null_value')"
            class="a-select-label">
            Ignore NULL Values?
          </label>
          <yes-no-select :id="selectedObjectModelProperty.idForProp('linked_relation_has_null_value')"
                         compress
                         v-model="selectedObjectModelProperty.linked_property_has_null_value"
                         :value="selectedObjectModelProperty.linked_property_has_null_value"></yes-no-select>

          <panel-item v-if="!selectedObjectModelProperty.linked_property_has_null_value">
            <label
              :for="selectedObjectModelProperty.idForProp('no_null_value')"
              class="a-select-label">
              Convert NULL Values to
            </label>
            <NumberInput
              :id="selectedObjectModelProperty.idForProp('no_null_value')"
              v-model="selectedObjectModelProperty.null_replacement_value">
            </NumberInput>
          </panel-item>
        </panel-item>
        <panel-item v-if="showAggregateNullLabelTitle">
          <label
            :for="selectedObjectModelProperty.idForProp('null_value')"
            class="a-select-label">
            {{ aggregateNullLabelTitle }}
          </label>
          <a-select
            :id="selectedObjectModelProperty.idForProp('null_value')"
            :options="optionsForNullValue"
            compress
            v-model="selectedObjectModelProperty.null_value"
          ></a-select>
        </panel-item>
      </template>

    </panel-block>

  </div>
</template>


<script lang="ts">
import Vue from "vue";
import {ObjectModelPropertyCapability} from "~/models/objectModelProperties/objectModelPropertyCapability";
import selectedMixin from "../mixins/selectedMixin";
import {IVOptionSetLocal} from "~/interfaces/vSelectInterfaces";
import ObjectModel from "~/models/objectModel";
import ConfigFieldValidation from "~/components/modelProperties/config-field-validation.vue";
import {EnumHelper} from "~/helpers/enumHelper";
import {
  ObjectModelBuiltinPropertyType,
  ObjectModelBuiltinPropertyTypeName
} from "~/enums/objectModelBuiltinPropertyType";
import AggregatableConfig from "~/components/modelProperties/aggregatable-config.vue"
import YesNoSelect from "~/components/yesNoSelect.vue"
import ObjectModelConditionalFields from "~/models/objectModels/objectModelConditionalFields";
import ConditionalWrapper from "~/components/conditionalRuleBuilder/objectModelPropertyConditionalWrapper.vue";
import {AggregateMetadata, AggregateType} from "~/enums/aggregateType";
import {AggregateNullValueType, AggregateNullValueTypeToString} from "~/enums/AggregateNullValueType";
import NumberInput from "~/components/numberInput.vue";
import {ObjectModelPropertyRootType} from "~/enums/objectModelPropertyType";
import {IConditionalRuleOptions} from "~/interfaces/modeling/conditionalRuleInterfaces";
import ObjectModelProperty from "~/models/objectModelProperties/objectModelProperty";

export default Vue.extend({
  mixins: [selectedMixin],
  components: {
    ConfigFieldValidation,
    AggregatableConfig,
    YesNoSelect,
    ConditionalWrapper,
    NumberInput
  },
  data: function () {
    const conditionalRuleOptions: IConditionalRuleOptions = {
      showIsUpdatedComparison: true,
      showPreviousValueComparisonOptions: true,
    }

    return {
      ObjectModelPropertyCapability: ObjectModelPropertyCapability,
      ObjectModelConditionalFields: ObjectModelConditionalFields,
      conditionalRuleOptions,
    };
  },
  computed: {
    relationSearchOptions(): IVOptionSetLocal {
      return {
        labelKey: (rel) => {
          if (rel.inverse) {
            return rel.label_title
          } else {
            return rel.inverse_label_title
          }
        },
        valueKey: "client_id_inverse",
        options: this.selectedObjectModel.mappableObjectModelRelationsFor(this.selectedObjectModelProperty),
      }
    },
    showConfigureMappedProperty(): boolean{
      return this.selectedObjectModelProperty.can(ObjectModelPropertyCapability.linkToRelationPropertyable) &&
        this.selectedObjectModelProperty.hasLinkedRelation &&
        this.selectedObjectModelProperty.aggregate_type !== AggregateType.Count
    },
    linkedObjectModels(): ObjectModel[] {
      if (this.selectedObjectModelProperty.hasLinkedRelation) {

        if (!this.selectedObjectModelProperty.linked_object_model_relation.root_object_model || this.selectedObjectModelProperty.linked_object_model_relation.rootObjectModel.id === this.selectedObjectModel.id) {
          return this.selectedObjectModelProperty.linked_object_model_relation.related_object_models;
        }

        return [this.selectedObjectModelProperty.linked_object_model_relation.rootObjectModel]
      } else {
        return [];
      }
    },
    optionsForNullValue(): IVOptionSetLocal {
      return {
        valueKey: "value",
        labelKey: "label",
        options: EnumHelper.toArray(AggregateNullValueType).map(type => {
            return {value: type, label: AggregateNullValueTypeToString[type]}
          }
        )
      }
    },
    aggregateNullLabelTitle() {
      const aggType = AggregateMetadata[this.selectedObjectModelProperty.aggregate_type].label;
      const labelTitle = `If ${aggType.toUpperCase()} Results in NULL...`
      return labelTitle;
    },
    displayNullOptions() {
      return (this.selectedObjectModelProperty.property_type === ObjectModelPropertyRootType.Aggregate) && this.selectedObjectModelProperty.aggregate_type !== AggregateType.Count
    },
    showAggregateNullLabelTitle() {
      const property = this.selectedObjectModelProperty;
      const hasAggregateType = !!property.aggregate_type;
      const isAllowedPropertyType = ![
        ObjectModelPropertyRootType.RelationshipCount,
        ObjectModelPropertyRootType.MappedProperty,
      ].includes(property.property_type);
      return hasAggregateType && isAllowedPropertyType;
    }
  },
  methods: {
    addLinkedPropertyRelation(selectedProperty, objectModel: ObjectModel) {
      const linkedRelationMappedProperty = this.selectedObjectModelProperty.linked_relation_mapped_property(objectModel.client_id);
      if (linkedRelationMappedProperty) {
        this.selectedObjectModelProperty.updateLinkedPropertyRelation(linkedRelationMappedProperty, selectedProperty)
      } else {
        let json
        if (selectedProperty.default_property){
          json= {property_client_id: null , root_model_client_id: objectModel.client_id, default_property: true, default_property_type: selectedProperty.client_id }
        }else{
          json= {property_client_id: selectedProperty.client_id, root_model_client_id: objectModel.client_id,  default_property: false}
        }
        this.selectedObjectModelProperty.addNewLinkedPropertyRelation(json)
      }
    },
    addLinkedRelationMappedPropertiesForCount(){
      this.linkedObjectModels.forEach(objectModel =>{
        let json = {property_client_id: null , root_model_client_id: objectModel.client_id, default_property: false, default_property_type: null }
        if (!this.selectedObjectModelProperty.linked_relation_mapped_properties.some(x=> x.root_model_client_id !== objectModel.clientId)){
          this.selectedObjectModelProperty.addNewLinkedPropertyRelation(json)
        }
      })
    },
    onAggregateSelection(event){
      if ( this.selectedObjectModelProperty.aggregate_type === AggregateType.Count){
        this.addLinkedRelationMappedPropertiesForCount()
      }
      if (this.selectedObjectModelProperty.aggregate_type === AggregateType.Count || this.selectedObjectModelProperty.aggregate_type === AggregateType.Sum) {
        this.selectedObjectModelProperty.null_value = AggregateNullValueType.SubstituteWithZero
      }else{
        this.selectedObjectModelProperty.null_value = AggregateNullValueType.LeaveAsNull
      }
    },
    mappedPropertyValue(objectModel: ObjectModel) {
      const linkedObjectModelRelationProperty = this.selectedObjectModelProperty.linked_relation_mapped_property(objectModel.client_id);
      if (linkedObjectModelRelationProperty) {
        return linkedObjectModelRelationProperty.referenceId
      }
    },
    aggregatePropertyHasFilter(objectModel: ObjectModel){
      const linkedObjectModelRelationProperty = this.selectedObjectModelProperty.linked_relation_mapped_property(objectModel.client_id);
      if (linkedObjectModelRelationProperty) {
        return linkedObjectModelRelationProperty.linked_property_has_filter
      }
    },
    aggregatePropertyHasNullValue(objectModel: ObjectModel){
      const linkedObjectModelRelationProperty = this.selectedObjectModelProperty.linked_relation_mapped_property(objectModel.client_id);
      if (linkedObjectModelRelationProperty) {
        return linkedObjectModelRelationProperty.linked_property_has_null_value
      }
    },
    linkedRelationMappedProperty(objectModel: ObjectModel) {
      const linkedObjectModelRelationProperty = this.selectedObjectModelProperty.linked_relation_mapped_property(objectModel.client_id);
      return linkedObjectModelRelationProperty
    },
    objectModelPropertyWithDefaultVOptionSetsFor(objectModel: ObjectModel): IVOptionSetLocal[] {
      return [
        this.objectModelPropertyVOptionSetsFor(objectModel),
        this.objectModelDefaultPropertyVOptionSetsFor(objectModel)
      ];
    },
    objectModelPropertyVOptionSetsFor(objectModel: ObjectModel): IVOptionSetLocal {
      let options: ObjectModelProperty[];
      if (this.selectedObjectModelProperty && this.selectedObjectModelProperty.property_type === ObjectModelPropertyRootType.MappedProperty) {
        // configuring a mapped property to reference itself leads to stack overflow in Vue
        options = objectModel.objectModelPropertiesArray.filter(prop => prop.client_id !== this.selectedObjectModelProperty.client_id)
      } else {
        options = objectModel.objectModelPropertiesArray
      }
      return {
        heading: "",
        valueKey: "client_id",
        labelKey: "labelTitleDisplay",
        options: options,
      };
    },
    objectModelDefaultPropertyVOptionSetsFor(objectModel: ObjectModel): IVOptionSetLocal {
      //Was running into issues having different Value keys for this and objectModelPropertyVOptionSetsFor which is why
      //I keyed the type as client_id
      return {
        heading: "About Record",
        valueKey: "client_id",
        labelKey: "labelTitleDisplay",
        options: EnumHelper.toArray(ObjectModelBuiltinPropertyType).map(type => {
            return {client_id: type, default_property: true, labelTitleDisplay: ObjectModelBuiltinPropertyTypeName[type]}
          }
        )
      }
    },
    setRelationOption(relation) {
      this.selectedObjectModelProperty.inverse = relation.inverse
      this.selectedObjectModelProperty.linked_relation_client_id = relation.client_id
      if ( this.selectedObjectModelProperty.aggregate_type === AggregateType.Count){
        this.addLinkedRelationMappedPropertiesForCount()
      }
    },
    getRelationOption() {
      const relation = this.selectedObjectModel.allRelations.find(r => r.client_id === this.selectedObjectModelProperty.linked_relation_client_id && r.inverse === this.selectedObjectModelProperty.inverse)
      if (!relation) return null
      return relation.client_id_inverse
    }
  },
  watch: {
    'selectedObjectModelProperty.linked_relation_client_id': function (newVal, oldVal) {
      this.selectedObjectModelProperty.clearAllLinkedObjectModelProperty();
    }
  }
});
</script>